import { formatPrice, isAndroid } from "@stepapp/core";
import { Block, Button, palette, SvgIcon, spacing, Touchable, Typography } from "@stepapp/ui";
import { useRouter } from "expo-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { UpdatePayment } from "../components/Payment/UpdatePayment";
import { AppStorage } from "../services/AppStorage";

type RetryPaymentProps = {
  paymentMethodId: string;
  selectPaymentMethod: (v: string) => void;
  price?: number;
  currency?: string;
  paymentId: string;
  reservationId: string;
  onClose: () => void;
};

export default function RetryPayment({
  onClose,
  paymentMethodId,
  selectPaymentMethod,
  price,
  currency,
  paymentId,
  reservationId,
}: RetryPaymentProps) {
  const { t } = useTranslation();
  const { replace } = useRouter();
  const priceFormatter = formatPrice(AppStorage().get("countryCode"));

  const [updatePaymentStep, setUpdatePaymentStep] = useState("paymentFailed");

  const handleClose = useCallback(() => {
    if (updatePaymentStep === "success") {
      return replace("/calendar");
    }
    setUpdatePaymentStep("paymentFailed");
    onClose();
  }, [updatePaymentStep, replace, onClose]);

  return (
    <>
      {!isAndroid && (
        <Touchable onPress={handleClose} style={styles.closeButton}>
          <Block alignCenter justifyCenter>
            <SvgIcon color={palette.font.primary} name="close" size={28} />
          </Block>
        </Touchable>
      )}

      {updatePaymentStep === "paymentFailed" && (
        <Block flex={1} justifyCenter marginVertical={1}>
          <Block alignCenter>
            <Block marginBottom={3} paddingVertical={2}>
              <SvgIcon color={palette.secondary.wrongPink} name="alert-filled" size={65} />
            </Block>
            <Typography center variant="newH2">
              {t("screens.calendar.failedCardPayment")}
            </Typography>
            <Block marginBottom={3} marginTop={1.5}>
              <Typography type="secondary" variant="lightLabel">
                {t("screens.calendar.additionalCardVerification")}
              </Typography>
            </Block>
          </Block>
          <Button
            label={t("screens.calendar.updatePayment")}
            onPress={() => setUpdatePaymentStep("settlePayment")}
            type="primary"
          />
        </Block>
      )}

      {updatePaymentStep === "settlePayment" && (
        <Block marginVertical={1} flex={1}>
          <Typography variant="newH2">
            {t("screens.calendar.settlePayment")}{" "}
            {priceFormatter(price, {
              includeRest: true,
              currency,
            })}
          </Typography>
          <Block marginBottom={3} marginTop={1.5}>
            <Typography type="secondary" variant="lightLabel">
              {t("screens.calendar.paymentFailedDescription")}
            </Typography>
          </Block>
          <UpdatePayment
            paymentId={paymentId}
            paymentMethodId={paymentMethodId}
            selectPaymentMethod={selectPaymentMethod}
            onRetryPaymentSuccess={() => setUpdatePaymentStep("success")}
            reservationId={reservationId}
          />
        </Block>
      )}
      {updatePaymentStep === "success" && (
        <Block flex={1}>
          <Block alignCenter justifyEnd flex={1} marginHorizontal={2.5}>
            <Block alignCenter justifyCenter marginVertical={3} style={styles.successIcon}>
              <SvgIcon color={palette.common.white} name="done" size={50} />
            </Block>
            <Typography center variant="newH2">
              {t("verification.paymentVerificationConfirmed")}
            </Typography>
          </Block>

          <Button
            label={t("screens.serviceDetails.close")}
            onPress={handleClose}
            type="primary"
            style={styles.successButton}
          />
        </Block>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  closeButton: {
    backgroundColor: palette.secondary.grey,
    borderRadius: spacing.unit * 2.5,
    height: spacing.unit * 5,
    justifyContent: "center",
    width: spacing.unit * 5,
    marginTop: spacing.unit * 2.5,
  },
  container: {
    flex: 1,
  },
  successButton: {
    flex: 1,
    justifyContent: "flex-end",
    paddingBottom: spacing.unit * 2,
  },
  successIcon: {
    backgroundColor: palette.primary.main,
    borderRadius: spacing.unit * 4,
    height: spacing.unit * 6.635,
    width: spacing.unit * 6.75,
  },
});
